import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articlesStore, localizeArticle } from "@/store/articlesStore";
import type { ManagedArticle } from "@/store/articlesStore";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import LanguageTranslatorButton from "./LanguageTranslatorButton";
import englishLogo from "../../../En logo copy2.png";

const REMOTE_DEFAULT_LOGO =
  "https://static.readdy.ai/image/433f60a06dda16b0e3fd209711ce4024/45c8d4d7d672c9e272b7c78f92025684.png";
const LOCAL_ARABIC_LOGO = `${__BASE_PATH__.replace(/\/?$/, "/")}assets/ar-logo.png`;

function resolveArabicLogo(configuredLogo?: string) {
  // The legacy default is a 21,861px-wide, 1.5 MB image. Even from cache its
  // enormous decode finishes well after first paint, making the header look
  // like the last part of the page to load.
  if (!configuredLogo || configuredLogo === REMOTE_DEFAULT_LOGO) return LOCAL_ARABIC_LOGO;
  return configuredLogo;
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language !== "en";
  const location = useLocation();
  const navigate = useNavigate();

  // isHome must be defined before any useEffect that uses it
  const isHome = location.pathname === "/";

  const { settings: siteSettings } = useSiteSettings();
  const [scrolled, setScrolled] = useState(!isHome); // transparent only on homepage
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // إغلاق القائمة والبحث عند تغيير الصفحة (مهم جداً للهواتف)
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setSearchVal("");
    setShowDropdown(false);
  }, [location.pathname]);
  const [searchVal, setSearchVal] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [quickResults, setQuickResults] = useState<ManagedArticle[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Immediately set correct state based on current scroll + page
    const check = () => {
      if (!isHome) {
        setScrolled(true);
      } else {
        setScrolled(window.scrollY > 60);
      }
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, [isHome]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchQuickResults = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setQuickResults([]);
      setShowDropdown(false);
      return;
    }
    setSearchLoading(true);
    const results = await articlesStore.search(q);
    setQuickResults(results.map((article) => localizeArticle(article, i18n.language)).slice(0, 5));
    setSearchLoading(false);
    setShowDropdown(true);
  }, [i18n.language]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchVal.trim()) {
      setShowDropdown(false);
      setSearchOpen(false);
      navigate(`/search?q=${encodeURIComponent(searchVal.trim())}`);
    }
  };

  const handleInputChange = (val: string) => {
    setSearchVal(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => fetchQuickResults(val), 300);
  };

  const handleResultClick = () => {
    setShowDropdown(false);
    setSearchOpen(false);
    setSearchVal("");
  };

  const isEnglish = i18n.language === "en";
  const logoSrc = isEnglish ? englishLogo : resolveArabicLogo(siteSettings?.logoUrl);
  const siteName = isEnglish ? t("brand.name") : siteSettings?.siteName || t("brand.name");

  // Navbar is transparent ONLY on homepage when user hasn't scrolled
  const isTransparent = isHome && !scrolled;

  const navLinks = [
    { labelKey: "nav.home", path: "/" },
    { labelKey: "nav.aviation", path: "/category/aviation" },
    { labelKey: "nav.hotels", path: "/category/hotels" },
    { labelKey: "nav.destinations", path: "/category/destinations" },
    { labelKey: "nav.videos", path: "/videos" },
    { labelKey: "nav.gcc", path: "/gcc-guide" },
    { labelKey: "nav.opinion", path: "/opinion" },
    { labelKey: "nav.about", path: "/about" },
    { labelKey: "nav.advertise", path: "/advertising" },
    { labelKey: "nav.pdfEdition", path: "/newspaper-pdf" },
  ];

  return (
    <>
      {/* Spacer: the navbar is position:fixed, so without this every non-home
          page would render its first content under the navbar. The homepage
          intentionally lets its hero peek through the transparent navbar, so we
          skip the spacer there. Heights match the navbar:
          mobile = h-16 (64px main bar; top bar is hidden under md)
          md+    = h-28 (32px top bar + 80px main bar = 112px) */}
      {!isHome && <div aria-hidden className="h-16 md:h-28" />}
    <header
      dir={isRtl ? "rtl" : "ltr"}
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
        !isTransparent
          ? "bg-white/96 backdrop-blur-md border-b-0"
          : "bg-transparent"
      }`}
    >
      {/* Top bar — shown when not transparent */}
      {!isTransparent && (
        <div className="bg-gradient-to-r from-[#1a1200] via-[#2a1e00] to-[#1a1200] border-b border-[#D4AF37]/20 hidden md:block">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10 flex items-center justify-between h-8">
            <div className="flex items-center gap-4">
              <span className="text-[#D4AF37]/70 text-xs flex items-center gap-1.5">
                <i className="ri-calendar-line text-[#D4AF37]/50" />
                {t("nav.topBarDate")}
              </span>
              <span className="text-[#D4AF37]/30 text-xs">|</span>
              <span className="text-[#D4AF37]/60 text-xs">{t("nav.topBarSlogan")}</span>
            </div>
            <div className="flex items-center gap-3">
              {[
                { icon: "ri-twitter-x-line", href: "#" },
                { icon: "ri-instagram-line", href: "#" },
                { icon: "ri-facebook-circle-line", href: "#" },
                { icon: "ri-youtube-line", href: "#" },
              ].map(({ icon, href }) => (
                <a
                  key={icon}
                  href={href}
                  className="text-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors cursor-pointer text-xs"
                >
                  <i className={icon} />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 relative">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img
              src={logoSrc}
              alt={siteName}
              width="140"
              height="40"
              loading="eager"
              fetchPriority="high"
              className={`h-10 w-auto object-contain transition-all ${
                isTransparent && !isEnglish ? "brightness-0 invert" : ""
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-3.5 py-2 text-sm font-bold transition-all duration-200 rounded-lg whitespace-nowrap ${
                  !isTransparent
                    ? "text-[#D4AF37] hover:text-[#b8962e] hover:bg-[#D4AF37]/5"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                } ${
                  location.pathname === link.path && link.path !== "/" && !isTransparent
                    ? "text-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_0_12px_rgba(212,175,55,0.35)]"
                    : location.pathname === link.path && link.path !== "/" && isTransparent
                    ? "text-white bg-white/15"
                    : ""
                }`}
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Search — Desktop */}
            <div className="hidden md:block">
              <LanguageTranslatorButton isTransparent={isTransparent} />
            </div>

            <div className="relative hidden md:block" ref={searchRef}>
              {searchOpen ? (
                <form onSubmit={handleSearchSubmit}>
                  <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-gray-200">
                    <i className="ri-search-line text-gray-400 text-sm" />
                    <input
                      ref={inputRef}
                      className="text-sm text-gray-700 outline-none w-52 bg-transparent placeholder:text-gray-400"
                      placeholder={t("nav.search")}
                      value={searchVal}
                      onChange={(e) => handleInputChange(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="cursor-pointer text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setSearchOpen(false);
                        setSearchVal("");
                        setShowDropdown(false);
                      }}
                    >
                      <i className="ri-close-line text-lg" />
                    </button>
                  </div>

                  {/* Dropdown */}
                  {showDropdown && (
                    <div
                      className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50"
                      style={{ minWidth: "320px" }}
                    >
                      {searchLoading ? (
                        <div className="px-4 py-5 text-center">
                          <i className="ri-loader-4-line text-2xl text-[#D4AF37] block mb-2 animate-spin" />
                          <p className="text-sm text-gray-400">{t("nav.searchLoading")}</p>
                        </div>
                      ) : quickResults.length > 0 ? (
                        <>
                          <div className="px-4 py-2.5 border-b border-gray-50 flex items-center justify-between">
                            <span className="text-xs text-gray-400 font-semibold">
                              {t("nav.searchResults")}
                            </span>
                            <span className="text-xs text-gray-300">
                              {quickResults.length}
                            </span>
                          </div>
                          {quickResults.map((article) => (
                            <Link
                              key={article.id}
                              to={`/article/${article.slug}`}
                              onClick={handleResultClick}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-0"
                            >
                              <div className="w-10 h-10 shrink-0 rounded-lg overflow-hidden">
                                <img
                                  src={article.image}
                                  alt={article.title}
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-800 font-medium line-clamp-1">
                                  {article.title}
                                </p>
                                <p className="text-xs text-[#D4AF37] mt-0.5">
                                  {article.category}
                                </p>
                              </div>
                              <i
                                className={`${
                                  isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"
                                } text-gray-300 shrink-0`}
                              />
                            </Link>
                          ))}
                          <button
                            type="submit"
                            className="w-full px-4 py-3 text-sm text-[#D4AF37] font-semibold hover:bg-[#D4AF37]/5 transition-colors cursor-pointer flex items-center justify-center gap-2 border-t border-gray-50"
                          >
                            <i className="ri-search-line" />
                            {t("nav.searchAll")} &ldquo;{searchVal}&rdquo;
                          </button>
                        </>
                      ) : (
                        <div className="px-4 py-5 text-center">
                          <i className="ri-search-eye-line text-2xl text-gray-300 block mb-2" />
                          <p className="text-sm text-gray-400">
                            {t("nav.searchEmpty")} &ldquo;{searchVal}&rdquo;
                          </p>
                          <button
                            type="submit"
                            className="mt-2 text-xs text-[#D4AF37] font-medium cursor-pointer hover:underline"
                          >
                            {t("nav.searchFull")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              ) : (
                <button
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                    !isTransparent
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-white/80 hover:bg-white/15"
                  }`}
                  onClick={() => {
                    setSearchOpen(true);
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                >
                  <i className="ri-search-line text-lg" />
                </button>
              )}
            </div>

            {/* Contact */}
            <Link
              to="/contact"
              className={`hidden md:flex w-9 h-9 items-center justify-center rounded-full transition-colors cursor-pointer ${
                !isTransparent
                  ? "text-gray-600 hover:bg-gray-100"
                  : "text-white/80 hover:bg-white/15"
              }`}
              aria-label={t("nav.contact")}
            >
              <i className="ri-customer-service-2-line text-lg" />
            </Link>

            {/* Hamburger */}
            <button
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${
                !isTransparent ? "text-gray-700" : "text-white"
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>
      {/* Golden divider line */}
      {!isTransparent && (
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-60" />
      )}

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-[560px] opacity-100" : "max-h-0 opacity-0"
        } bg-white border-t border-gray-100`}
        style={{ overscrollBehavior: "contain" }}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === link.path && link.path !== "/"
                  ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <LanguageTranslatorButton mobile />
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-customer-service-2-line text-[#D4AF37]" />
              {t("nav.contact")}
            </Link>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const val = (
                  e.currentTarget.querySelector("input") as HTMLInputElement
                ).value.trim();
                if (val) {
                  setMenuOpen(false);
                  navigate(`/search?q=${encodeURIComponent(val)}`);
                }
              }}
            >
              <div className="flex items-center gap-2 bg-gray-50 rounded-full px-3 py-2 border border-gray-200">
                <i className="ri-search-line text-gray-400 text-sm" />
                <input
                  className="text-sm text-gray-700 outline-none flex-1 bg-transparent placeholder:text-gray-400"
                  placeholder={t("nav.search")}
                />
                <button
                  type="submit"
                  className="w-7 h-7 flex items-center justify-center bg-[#D4AF37] rounded-full cursor-pointer shrink-0"
                >
                  <i
                    className={`${
                      isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"
                    } text-[#0A1628] text-sm`}
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </header>
    </>
  );
}
